/*
  BloomUp: own versions of four storefront behaviours whose theme script
  is not running: predictive search, quick-search links, back-to-top button
  and the country list filter. Written for BloomUp; independent of the
  theme's custom.js, which is left untouched.

  Each element is registered only when the theme has not already registered
  it, so this file steps aside if the theme script starts running again.
*/
(function () {
  'use strict';

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function lower(value) {
    return String(value || '').toLowerCase().trim();
  }

  /* ---------- Predictive search (search drawer) ---------- */

  class BuPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.buReady || !this.hasAttribute('enabled')) return;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-results-search]');
      if (!this.input || !this.results) return;
      this.buReady = true;

      this.skeleton = this.querySelector('[data-skeleton-search]');
      this.idlePanel = this.querySelector('.hdt-cart-hide-has-results');
      this.sectionId = this.getAttribute('data-section-id-results') || 'hdt_predictive-search';
      this.cache = {};
      this.request = null;

      var self = this;
      this.input.addEventListener('input', debounce(function () { self.search(); }, 300));
    }

    endpoint(term) {
      var routes = (window.themeHDN && window.themeHDN.routes) || {};
      var base = routes.predictive_search_url || '/search/suggest';
      var params = new URLSearchParams();
      params.set('q', term);
      var form = this.input.form;
      if (form) {
        form.querySelectorAll('input[type="hidden"][name^="resources"]').forEach(function (field) {
          params.append(field.name, field.value);
        });
      }
      params.set('section_id', this.sectionId);
      return base + '?' + params.toString();
    }

    search() {
      var term = this.input.value.trim();
      if (!term) {
        this.reset();
        return;
      }
      if (this.cache[term]) {
        this.show(this.cache[term]);
        return;
      }

      if (this.request) this.request.abort();
      this.request = new AbortController();
      this.setLoading(true);

      var self = this;
      fetch(this.endpoint(term), { signal: this.request.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('predictive search ' + response.status);
          return response.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var section = doc.querySelector('[id="shopify-section-' + self.sectionId + '"]');
          var html = section ? section.innerHTML : doc.body.innerHTML;
          self.cache[term] = html;
          if (self.input.value.trim() !== term) return;
          self.show(html);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          self.setLoading(false);
          if (!self.hasAttribute('results') && self.idlePanel) self.idlePanel.style.display = '';
          console.error(error);
        });
    }

    setLoading(on) {
      if (on) {
        this.setAttribute('loading', 'true');
        if (this.idlePanel) this.idlePanel.style.display = 'none';
        if (this.skeleton && !this.hasAttribute('results')) this.skeleton.classList.remove('hdt-hidden');
      } else {
        this.removeAttribute('loading');
        if (this.skeleton) this.skeleton.classList.add('hdt-hidden');
      }
    }

    show(html) {
      this.results.innerHTML = html;
      this.setLoading(false);
      if (this.idlePanel) this.idlePanel.style.display = 'none';
      this.setAttribute('results', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      document.dispatchEvent(new CustomEvent('currency:update'));
    }

    reset() {
      if (this.request) this.request.abort();
      this.results.innerHTML = '';
      this.setLoading(false);
      if (this.idlePanel) this.idlePanel.style.display = '';
      this.removeAttribute('results');
      this.input.setAttribute('aria-expanded', 'false');
    }
  }

  /* ---------- Quick-search links inside the search drawer ---------- */

  class BuQuickSearch extends HTMLElement {
    connectedCallback() {
      if (this.buReady) return;
      this.buReady = true;
      var self = this;
      this.addEventListener('click', function (event) {
        var link = event.target.closest('a[data-query]');
        if (!link) return;
        var host = self.closest('hdt-predictive-search');
        var input = host ? host.querySelector('input[type="search"]') : null;
        if (!input) return;
        event.preventDefault();
        input.value = String(link.getAttribute('data-query') || '').trim();
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      });
    }
  }

  /* ---------- Back-to-top button ---------- */

  class BuBackToTop extends HTMLElement {
    connectedCallback() {
      if (this.buReady) return;
      this.buReady = true;

      var config = {};
      try {
        config = JSON.parse(this.getAttribute('config') || '{}') || {};
      } catch (error) {
        config = {};
      }
      if (config.hiddenMobile && window.matchMedia('(max-width: 767px)').matches) return;

      var self = this;
      var threshold = Number(config.scrollTop) || 100;
      var ring = this.querySelector('.hdt-circle--bg');
      var queued = false;

      var update = function () {
        queued = false;
        var y = window.scrollY || document.documentElement.scrollTop || 0;
        self.classList.toggle('is--show', y > threshold);
        if (ring) {
          var max = document.documentElement.scrollHeight - window.innerHeight;
          var ratio = max > 0 ? Math.min(1, Math.max(0, y / max)) : 0;
          ring.style.setProperty('--cricle-degrees', (ratio * 360).toFixed(1) + 'deg');
        }
      };

      window.addEventListener('scroll', function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(update);
      }, { passive: true });

      this.addEventListener('click', function (event) {
        event.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      update();
    }
  }

  /* ---------- Country list filter (currency / country popover) ---------- */

  class BuCountryFilter extends HTMLElement {
    connectedCallback() {
      if (this.buReady) return;
      var input = this.querySelector('input');
      if (!input) return;
      this.buReady = true;

      var scope = this.closest('dialog') || this.parentNode || document;
      var apply = debounce(function () {
        var term = lower(input.value);
        scope.querySelectorAll('[role="option"][data-name]').forEach(function (option) {
          var haystack = lower(option.getAttribute('data-name')) + ' ' + lower(option.textContent);
          option.style.display = (!term || haystack.indexOf(term) !== -1) ? '' : 'none';
        });
      }, 150);

      input.addEventListener('input', apply);
      input.addEventListener('search', apply);
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') event.preventDefault();
      });
    }
  }

  function register() {
    if (!window.customElements) return;
    var parts = [
      ['hdt-predictive-search', BuPredictiveSearch],
      ['hdt-quick-search', BuQuickSearch],
      ['back-to-top', BuBackToTop],
      ['country-filter', BuCountryFilter]
    ];
    parts.forEach(function (part) {
      if (!customElements.get(part[0])) customElements.define(part[0], part[1]);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', register);
  } else {
    register();
  }
})();